import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link, useLocation } from 'react-router-dom';
import { categoryImages } from '@/mocks/categoryImages';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isArticlePage = location.pathname !== '/';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/98 backdrop-blur-md border-b-2 border-background-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left - Logo + Nav Links */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <span className="font-heading text-xl md:text-2xl text-foreground-950 tracking-tight">
                Pandabuy<span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!isArticlePage ? 'bg-primary-50 text-primary-700' : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'}`}
              >
                Home
              </Link>
              <Link
                to="/pandabuy-spreadsheet-guide"
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isArticlePage ? 'bg-primary-50 text-primary-700' : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'}`}
              >
                Shopping Guide
              </Link>
            </div>
          </div>

          {/* Center - Desktop Category Dropdown */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-5 py-2.5 bg-background-100 border border-background-300 rounded-full text-sm text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              <i className="ri-grid-line text-base"></i>
              <span>Browse Categories</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[720px] max-h-[520px] overflow-y-auto bg-background-50 border border-background-200 rounded-2xl py-3 grid grid-cols-2 gap-0.5 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 mx-1 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-background-100">
                      <img
                        src={categoryImages[cat.imageSeq] || categoryImages['cat-other-14']}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-foreground-900 whitespace-nowrap group-hover:text-primary-600 transition-colors">{cat.name}</span>
                      <span className="text-xs text-foreground-500 truncate">{cat.description}</span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Right - CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-100 border border-background-200 text-foreground-700 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto animate-in slide-in-from-top-2">
            {/* Mobile Nav Links */}
            <div className="flex items-center gap-2 mb-4">
              <Link
                to="/"
                className={`flex-1 text-center px-4 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${!isArticlePage ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-700'}`}
              >
                <i className="ri-home-line mr-1"></i> Home
              </Link>
              <Link
                to="/pandabuy-spreadsheet-guide"
                className={`flex-1 text-center px-4 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${isArticlePage ? 'bg-primary-50 text-primary-700' : 'bg-background-100 text-foreground-700'}`}
              >
                <i className="ri-book-open-line mr-1"></i> Shopping Guide
              </Link>
            </div>

            {/* Mobile Categories */}
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2 px-1">Categories</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                    <img
                      src={categoryImages[cat.imageSeq] || categoryImages['cat-other-14']}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-foreground-900 leading-tight">{cat.name}</span>
                    <span className="text-[10px] text-foreground-500 truncate">{cat.description}</span>
                  </div>
                </a>
              ))}
            </div>

            {/* Mobile CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mt-4 w-full px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Pandabuy Sheet
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}